// ** React Imports
import { Fragment } from "react"
// ** Third Party Components
import AsyncSelect from "react-select/async"
import { useForm, Controller } from "react-hook-form"
import { ArrowLeft, ArrowRight, Info } from "react-feather"
import Cleave from "cleave.js/react"
import Flatpickr from "react-flatpickr"
import Nouislider from "nouislider-react"
// ** Utils
import { provideOptionsToSelect, checkFieldValidity } from "@lib/util"
import { BANNED_WORDS } from "@lib/constants"
// ** Reactstrap Imports
import {
  Label,
  Row,
  Col,
  Button,
  Form,
  Input,
  FormFeedback,
  Card,
  CardBody,
  Alert
} from "reactstrap"
// ** Styles
import "@styles/react/libs/react-select/_react-select.scss"
import "@styles/react/libs/flatpickr/flatpickr.scss"
import "@styles/react/libs/noui-slider/noui-slider.scss"
// ** Store
import { useDispatch, useSelector } from "react-redux"
import { setFormData } from "@store/stores/person"

const MIN_BIRTHDATE = "1900-01-01"
const DEFAULT_AGE_ESTIMATE = 50

const PersonalInfo = ({ stepper }) => {
  const store = useSelector((state) => state.persons)

  let defaultValues = {}
  if (store.selectedPerson) {
    // if it's the default DB date, fallback to minDate to avoid error
    let defaultDate = store.selectedPerson.birthDate
    if (defaultDate === "0001-01-01T00:00:00") {
      defaultDate = MIN_BIRTHDATE
    }
    defaultValues = {
      id: store.selectedPerson.id,
      isVIP: store.selectedPerson.isVIP,
      title: { label: store.selectedPerson.title.name, value: store.selectedPerson.title.id },
      gender: { label: store.selectedPerson.gender.name, value: store.selectedPerson.gender.id },
      firstName: store.selectedPerson.firstName,
      name: store.selectedPerson.name,
      language: {
        label: store.selectedPerson.language.name,
        value: store.selectedPerson.language.id
      },
      annualIncome: store.selectedPerson.annualIncome,
      isBirthDateExact: store.selectedPerson.isBirthDateExact,
      birthDate: store.selectedPerson.isBirthDateExact === true ? defaultDate : "",
      estimatedBirthDate: store.selectedPerson.isBirthDateExact === false ? defaultDate : "",
      ageEstimation: DEFAULT_AGE_ESTIMATE
    }
  } else {
    defaultValues = {
      isVIP: false,
      isBirthDateExact: false,
      ageEstimation: DEFAULT_AGE_ESTIMATE,
      name: "",
      firstName: "",
      annualIncome: ""
    }
  }
  // ** Hooks
  const dispatch = useDispatch()
  const {
    control,
    setError,
    register,
    setValue,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm({ defaultValues })

  // ** Watchers
  const watchIsBirthDateExact = watch("isBirthDateExact")
  const watchBirthDate = watch("birthDate")
  const watchestimatedBirthDate = watch("estimatedBirthDate")
  const birthDateIsNotValid = watchBirthDate?.length === 0 || watchBirthDate === undefined
  const estimatedBirthDateIsNotValid =
    watchestimatedBirthDate?.length === 0 || watchestimatedBirthDate === undefined

  // ** Local components
  const HelpNotification = (props) => {
    return (
      <Alert color="info">
        <div className="alert-body">
          <Info size={15} /> <span className="ms-1">{props.children}</span>
        </div>
      </Alert>
    )
  }

  const onSubmit = (data) => {
    // form validation
    let error = false
    const optionalFields = ["annualIncome", "birthDate", "estimatedBirthDate"]
    for (const key in data) {
      if (optionalFields.includes(key)) continue
      if (!checkFieldValidity(data[key])) {
        setError(key, {
          type: "manual",
          message: `'${key}' is required`
        })
        error = true
      }
      // specific tests on name and firstName
      if (key === "name" || key === "firstName") {
        let errorMessage
        // contains numbers or do not starts with a letter
        if (/\d/g.test(data[key]) || !/^[a-zA-Z]/.test(data[key][0])) {
          errorMessage = `${key} is invalid`
        }
        // contains a variation to describe an anonymous client
        if (BANNED_WORDS.anonymousClient.includes(data[key].toLowerCase())) {
          errorMessage = (
            <HelpNotification>
              <b>Adding an anonymous visit ?</b> Use the <a href="/CRM">Anonymous event</a> button.
            </HelpNotification>
          )
        } else if (BANNED_WORDS.showroom.includes(data[key].toLowerCase())) {
          // user probably tries to add a showroomOrder -> warn them
          errorMessage = (
            <HelpNotification>
              Ordering for an Elite Showroom? Please use{" "}
              <a href="/Configurator/ShowroomOrders">this form</a>.
            </HelpNotification>
          )
        }
        if (errorMessage) {
          setError(key, { type: "manual", message: errorMessage })
          error = true
        }
      }
    }
    if (error === false) {
      // UX for birthDate selection
      let birthDate
      if (watchIsBirthDateExact) {
        const rawDate = new Date(data.birthDate)
        birthDate = `${rawDate.getFullYear()}-${rawDate.getMonth() + 1}-${rawDate.getDate()}`
      } else {
        // if we edit user and birthDate is not exact
        if (store.selectedPerson && store.selectedPerson.isBirthDateExact === false) {
          // just forward the birthDate
          birthDate = store.selectedPerson.birthDate
        } else {
          // otherwise, do estimation based on ageEstimation
          const today = new Date()
          const estimatedBirthdate = new Date(
            today.setFullYear(today.getFullYear() - data.ageEstimation)
          )
          const year = estimatedBirthdate.getFullYear()
          const month = estimatedBirthdate.getMonth() + 1
          const day = estimatedBirthdate.getDate()
          birthDate = `${year}-${month}-${day}`
        }
      }
      // cleanup data shape
      const concatData = {
        ...data,
        birthDate,
        annualIncome: data.annualIncome ? data.annualIncome.toString().replace("'", "") : undefined,
        titleId: data.title.value,
        genderId: data.gender.value,
        languageId: data.language.value
      }
      // eslint-disable-next-line no-unused-vars
      const { title, gender, language, ageEstimation, estimatedBirthDate, ...cleanData } =
        concatData
      // dispatch action to update store
      dispatch(setFormData({ personalInfos: cleanData }))
      stepper.next()
    }
  }

  return (
    <Fragment>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex justify-content-between align-items-center">
          <div className="content-header">
            <h5 className="mb-0">Personal information</h5>
            <small>Basic informations about the person</small>
          </div>
          <div className="d-flex gap-2">
            <Button type="button" color="primary" className="btn-prev" disabled>
              <ArrowLeft size={14} className="align-middle me-sm-25 me-0"></ArrowLeft>
              <span className="align-middle d-sm-inline-block d-none">Previous</span>
            </Button>
            <Button
              type="submit"
              color="primary"
              className="btn-next"
              disabled={
                (watchIsBirthDateExact && birthDateIsNotValid) ||
                (!watchIsBirthDateExact &&
                  estimatedBirthDateIsNotValid &&
                  store.selectedPerson?.isBirthDateExact === false)
              }
            >
              <span className="align-middle d-sm-inline-block d-none">Next</span>
              <ArrowRight size={14} className="align-middle ms-sm-25 ms-0" />
            </Button>
          </div>
        </div>
        <hr />

        <Row>
          <Col md="6" className="mb-1">
            <div className="form-check form-switch">
              <Label for="isVIP" className="form-check-label">
                VIP
              </Label>
              <Controller
                name="isVIP"
                id="isVIP"
                control={control}
                render={({ field }) => <Input {...field} type="switch" />}
              />
            </div>
          </Col>
        </Row>
        <Row>
          <Col md="6" className="mb-1">
            <Label className="form-label">Title *</Label>
            <Controller
              name="title"
              id="title"
              control={control}
              render={({ field }) => (
                <AsyncSelect
                  {...field}
                  isClearable={false}
                  isSearchable={false}
                  className="react-select"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  loadOptions={() => provideOptionsToSelect({ name: "Titles" })}
                />
              )}
            />
            {errors.title && <small style={{ color: "#ea5455" }}>{errors.title.message}</small>}
          </Col>
          <Col md="6" className="mb-1">
            <Label className="form-label">Gender *</Label>
            <Controller
              name="gender"
              id="gender"
              control={control}
              render={({ field }) => (
                <AsyncSelect
                  {...field}
                  isClearable={false}
                  isSearchable={false}
                  className="react-select"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  // 16.02.24 : display only male and female
                  loadOptions={() =>
                    provideOptionsToSelect({ name: "Genders?filters[genderId]=lt_9" })
                  }
                />
              )}
            />
            {errors.gender && <small style={{ color: "#ea5455" }}>{errors.gender.message}</small>}
          </Col>
        </Row>
        <Row>
          <Col md="6" className="mb-1">
            <Label className="form-label" for="name">
              Last name *
            </Label>
            <Controller
              id="name"
              name="name"
              control={control}
              render={({ field }) => (
                <Input placeholder="Doe" invalid={errors.name && true} {...field} />
              )}
            />
            {errors.name && <FormFeedback>{errors.name.message}</FormFeedback>}
          </Col>
          <Col md="6" className="mb-1">
            <Label className="form-label" for="firstName">
              First name *
            </Label>
            <Controller
              id="firstName"
              name="firstName"
              control={control}
              render={({ field }) => (
                <Input placeholder="Jon" invalid={errors.firstName && true} {...field} />
              )}
            />
            {errors.firstName && <FormFeedback>{errors.firstName.message}</FormFeedback>}
          </Col>
        </Row>
        <Row>
          <Col md="6" className="mb-1">
            <Label className="form-label" for="language">
              Language *
            </Label>
            <Controller
              name="language"
              id="language"
              control={control}
              render={({ field }) => (
                <AsyncSelect
                  {...field}
                  isClearable={false}
                  isSearchable={true}
                  className="react-select"
                  classNamePrefix="select"
                  cacheOptions
                  defaultOptions
                  loadOptions={(inputValue) =>
                    provideOptionsToSelect({ name: `Languages?search=${inputValue}` })
                  }
                />
              )}
            />
            {errors.language && (
              <small style={{ color: "#ea5455" }}>{errors.language.message}</small>
            )}
          </Col>
          <Col md="6" className="mb-1">
            <Label className="form-label" for="annualIncome">
              Annual income
            </Label>
            <Controller
              id="annualIncome"
              name="annualIncome"
              control={control}
              render={({ field }) => (
                <Cleave
                  {...field}
                  className="form-control"
                  placeholder="12'345"
                  required={false}
                  options={{
                    numeral: true,
                    numeralThousandsGroupStyle: "thousand",
                    delimiter: "'"
                  }}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mb-2">
          <Label className="form-label" for="birthDate">
            Birthdate *
          </Label>
          <Col>
            <Card>
              <CardBody>
                <div>
                  <div className="d-flex items-align-center gap-1">
                    <Input
                      {...register("isBirthDateExact")}
                      id="isBirthDateExact-false"
                      type="radio"
                      role="button"
                      checked={!watchIsBirthDateExact}
                      onChange={() => {
                        setValue("isBirthDateExact", false)
                      }}
                    />
                    <Label className="form-check-label fw-bold" for="isBirthDateExact-false">
                      Age estimation
                    </Label>
                  </div>
                  <Input type="hidden" name="ageEstimation" {...register("ageEstimation")} />
                  {store.selectedPerson?.isBirthDateExact === false ? (
                    <>
                      <Label className="form-label mt-2" for="birthDate">
                        Birthdate was estimated to :
                      </Label>
                      <Controller
                        name="estimatedBirthDate"
                        id="estimatedBirthDate"
                        control={control}
                        render={({ field }) => (
                          <Flatpickr
                            {...field}
                            disabled={watchIsBirthDateExact}
                            placeholder="Estimate a birthdate"
                            className="form-control"
                            options={{
                              dateFormat: "Y-m-d",
                              minDate: MIN_BIRTHDATE,
                              maxDate: "today",
                              enableTime: false
                            }}
                          />
                        )}
                      />
                    </>
                  ) : (
                    <Nouislider
                      style={{ zIndex: 0, cursor: "pointer" }}
                      disabled={watchIsBirthDateExact}
                      id="ageEstimationSlider"
                      name="ageEstimationSlider"
                      className="mt-4"
                      connect={[true, false]}
                      step={10}
                      start={DEFAULT_AGE_ESTIMATE}
                      onChange={(values) => {
                        setValue("ageEstimation", values[0])
                      }}
                      tooltips={{
                        to(value) {
                          return `${value.toFixed(0)} years`
                        },
                        from(value) {
                          return value.toFixed(0)
                        }
                      }}
                      format={{
                        to(value) {
                          return parseInt(value.toFixed(0))
                        },
                        from(value) {
                          return value
                        }
                      }}
                      range={{ min: 10, max: 90 }}
                    />
                  )}
                </div>
                <div className="divider divider-primary">
                  <div className="divider-text">OR</div>
                </div>
                <div>
                  <div className="d-flex items-align-center gap-1">
                    <Input
                      {...register("isBirthDateExact")}
                      id="isBirthDateExact-true"
                      type="radio"
                      role="button"
                      checked={watchIsBirthDateExact}
                      onChange={() => {
                        setValue("isBirthDateExact", true)
                      }}
                    />
                    <Label className="form-check-label fw-bold" for="isBirthDateExact-true">
                      Exact birthdate
                    </Label>
                  </div>
                  <Label className="form-label mt-2" for="birthDate">
                    Enter the exact birthdate :
                  </Label>
                  <Controller
                    name="birthDate"
                    id="birthDate"
                    control={control}
                    render={({ field }) => (
                      <Flatpickr
                        {...field}
                        disabled={!watchIsBirthDateExact}
                        placeholder={
                          !watchIsBirthDateExact ? "Age estimation active" : "Select a birthdate"
                        }
                        className="form-control"
                        options={{
                          dateFormat: "Y-m-d",
                          minDate: MIN_BIRTHDATE,
                          maxDate: "today",
                          enableTime: false
                        }}
                      />
                    )}
                  />
                  {watchBirthDate?.length === "" && (
                    <small style={{ color: "#ea5455" }}>Birthdate is required</small>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Form>
    </Fragment>
  )
}

export default PersonalInfo
